import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Editing,
    FilterRow,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch,
    Toolbar,
    Item,
    Lookup
} from "devextreme-react/data-grid";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { LoadPanel } from 'devextreme-react/load-panel';
import { GridLayout } from "../../../../common/GridLayout";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';

function DetailsTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    const detailGrid = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [currentLayout, setCurrentLayout] = useState("Default");
    const gridKey = "3F568555-0667-4467-8EB5-96471FFE1B8E";

    const position = { of: '#accordion' };

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
    }, [currentLayout, accessToken]);

    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout);
    }, [gridKey, currentLayout, accessToken]);

    const makeLookupStore = useCallback((loadUrl, loadParams) => {
        return createStore({
            key: 'id',
            loadUrl: `${window._env_.REACT_APP_APIS}/${loadUrl}`,
            loadParams: loadParams || {},
            onBeforeSend: function (method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                    access_token: accessToken,
                };
            }
        });
    }, [accessToken]);

    const vendorLookup = useMemo(() =>
        makeLookupStore('common/Lookup/GetLookup', { module: 'ID', lookupType: 'Vendor', showLookupGrid: false, dropShip: '', sort: '[{"selector":"identityId","desc":false}]' }),
        [makeLookupStore]);

    const accountLookup = useMemo(() =>
        makeLookupStore(data.AC?.GetAcAccounts || 'ac/Lookup/GetAcAccounts'),
        [makeLookupStore]);

    const departmentLookup = useMemo(() =>
        makeLookupStore(data.AC?.GetDepartments || 'ac/Lookup/GetDepartments'),
        [makeLookupStore]);

    const jobLookup = useMemo(() =>
        makeLookupStore(data.CA?.GetCaJobs || 'ca/Lookup/GetCaJobs'),
        [makeLookupStore]);

    const fundLookup = useMemo(() =>
        makeLookupStore(data.FA?.GetFafunds || 'fa/Lookup/GetFafunds'),
        [makeLookupStore]);

    const activityLookup = useMemo(() =>
        makeLookupStore('ca/Lookup/GetCAActivity'),
        [makeLookupStore]);

    const distributionLookup = useMemo(() =>
        makeLookupStore('common/Lookup/GetLookup', { module: 'AC', lookupType: 'Distribution', showLookupGrid: false, usageModule: 'OM' }),
        [makeLookupStore]);

    const [detailCodeLookup, setDetailCodeLookup] = useState([]);

    useEffect(() => {
        if (!accessToken) return;
        const url = `${window._env_.REACT_APP_APIS}/${data.ID?.GetIDCodes || 'id/Lookup/GetIDCodes'}?codeType=ZU`;
        fetch(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                access_token: accessToken,
            }
        })
            .then(res => res.json())
            .then(result => {
                setDetailCodeLookup(result.data || result);
            });
    }, [accessToken]);

    const baseUrl = window._env_.REACT_APP_APIS;

    const dataSource = useMemo(() => {
        if (!props.headerId || props.headerId === '00000000-0000-0000-0000-000000000000') {
            return [];
        }

        return createStore({
            key: 'RowId',
            loadUrl: `${baseUrl}/${data.ID?.GetTaxDetails || 'id/TaxDetail/GetTaxDetails'}`,
            insertUrl: `${baseUrl}/${data.ID?.AddTaxDetail || 'id/TaxDetail/AddTaxDetail'}`,
            updateUrl: `${baseUrl}/${data.ID?.EditTaxDetail || 'id/TaxDetail/EditTaxDetail'}`,
            deleteUrl: `${baseUrl}/${data.ID?.DeleteTaxDetail || 'id/TaxDetail/DeleteTaxDetail'}`,
            loadParams: { headerRowId: props.headerId },
            deleteMethod: "DELETE",
            onBeforeSend: function (method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                };
            }
        });
    }, [props.headerId, accessToken, baseUrl]);

    const onRowInserting = useCallback((e) => {
        e.data.RTaxHeader = props.headerId;
    }, [props.headerId]);

    const onInitNewRow = useCallback((e) => {
        e.data.RTaxHeader = props.headerId;
    }, [props.headerId]);

    return (
        <div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />
            <DataGrid
                dataSource={dataSource}
                ref={detailGrid}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onInitNewRow={onInitNewRow}
                onRowInserting={onRowInserting}
                onRowInserted={() => props.onDetailsSaved?.()}
                onRowUpdated={() => props.onDetailsSaved?.()}
                onRowRemoved={() => props.onDetailsSaved?.()}
                height="50vh"
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    allowAdding={true}
                    allowUpdating={true}
                    allowDeleting={true}
                    confirmDelete={true}
                    newRowPosition="last"
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} showInColumnChooser={false} />
                <Column dataField="RTaxHeader" caption="Header ID" visible={false} showInColumnChooser={false} />
                <Column dataField="DetailCode" caption="Code" dataType="string" allowSorting={false}
                    calculateCellValue={(rowData) => rowData.DetailCode?.trim()}
                >
                    <Lookup dataSource={detailCodeLookup} valueExpr="name" displayExpr="name" />
                </Column>
                <Column dataField="RVendor" caption="Vendor" allowSorting={false}>
                    <Lookup dataSource={vendorLookup} valueExpr="id" displayExpr="identityId" />
                </Column>
                <Column dataField="TaxRate" caption="Tax % Rate" dataType="number" allowSorting={false} format="#0.######" />
                <Column dataField="RDistributionCode" caption="Distribution" allowSorting={false}>
                    <Lookup dataSource={distributionLookup} valueExpr="id" displayExpr="name" />
                </Column>
                <Column dataField="RAccount" caption="Account" allowSorting={false}>
                    <Lookup dataSource={accountLookup} valueExpr="id" displayExpr="name" />
                </Column>
                <Column dataField="RGLDepartment" caption="Department" allowSorting={false}>
                    <Lookup dataSource={departmentLookup} valueExpr="id" displayExpr="name" />
                </Column>
                <Column dataField="RFund" caption="Fund" allowSorting={false}>
                    <Lookup dataSource={fundLookup} valueExpr="id" displayExpr="name" />
                </Column>
                <Column dataField="Description1" caption="Description" dataType="string" allowSorting={false} />
                <Column dataField="EffectiveDateStart" caption="Start Date" dataType="date" allowSorting={false} />
                <Column dataField="EffectiveDateEnd" caption="End Date" dataType="date" allowSorting={false} />
                <Column dataField="DetailGroup" caption="Group" dataType="string" allowSorting={false} />
                <Column dataField="DetailRank" caption="Rank" dataType="number" allowSorting={false} />
                <Column dataField="Comment" caption="Comment" dataType="string" allowSorting={false} />
                <Column dataField="RCAJob" caption="CA Job" visible={false} allowSorting={false}>
                    <Lookup dataSource={jobLookup} valueExpr="id" displayExpr="name" />
                </Column>
                <Column dataField="RActivity" caption="Activity" visible={false} allowSorting={false}>
                    <Lookup dataSource={activityLookup} valueExpr="id" displayExpr="name" />
                </Column>
                <Column dataField="KeySequence" caption="Record #" dataType="number" visible={false} allowSorting={false} allowEditing={false} />
                <Column dataField="CreationDateTime" caption="Creation Date" dataType="datetime" visible={false} allowSorting={false} allowEditing={false} />
                <Column dataField="CreationUserId" caption="Creation User" dataType="string" visible={false} allowSorting={false} allowEditing={false} />
                <Column dataField="LastUpdateDateTime" caption="Last Update Date" dataType="datetime" visible={false} allowSorting={false} allowEditing={false} />
                <Column dataField="LastUpdateUserId" caption="Last Update User" dataType="string" visible={false} allowSorting={false} allowEditing={false} />
                <Column dataField="CompanyId" caption="Company" dataType="string" visible={false} allowSorting={false} allowEditing={false} />
                <Column dataField="Module" caption="Module" dataType="string" visible={false} allowSorting={false} allowEditing={false} />
                <Column dataField="RecordType" caption="Record Type" dataType="string" visible={false} allowSorting={false} allowEditing={false} />
                <Toolbar>
                    <Item name="addRowButton" />
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={detailGrid} setCurrentLayout={setCurrentLayout} />
                    </Item>
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(DetailsTab);
